/* eslint-disable no-unused-vars */
import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { getNextYear, toISODate } from '../../helpers/dateTime';
import { Rule, Rules } from '../../types/Rules';
import { DateTime } from 'luxon';

export interface VoucherFormState {
  activeDiscount: string;
  payload: {
    count: number;
    specification: string;
    prefix: string;
    codeLength: number;
    discountType: DiscountType;
    validSince: string;
    validUntil: string;
    rules: Rules;
    entity: Entity | null;
    multiUsage: boolean;
  };
  comment?: Comment;
}

export type Comment = string | null;

export type Entity =
  | 'flix_brazil'
  | 'flix_se'
  | 'flix_chile'
  | 'flix_india'
  | 'flix_mexico'
  | 'flix_inc'
  | 'kamil_koc';

// ToDo: Refactor DiscountType by splitting it into Separate Types
interface DiscountType {
  type: string;
  percentage?: number;
  currency?: string;
  discount?: number;
  credit?: number;
  quantity?: number;
}

type PayloadTypes = string | number | DiscountType;

interface VoucherFormContextType {
  formState: VoucherFormState;
  setFormState: React.Dispatch<React.SetStateAction<VoucherFormState>>;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>, prop: string) => void;
  handlePayloadChange: (params: { value: PayloadTypes; prop: string }) => void;
  handleRuleChange: <T extends Rule>(updatedRule: T, removeRule?: boolean, ruleId?: string) => void;
  handleActiveDiscountChange: (newType: string) => void;
  handleEntityChange: (entity: Entity | null) => void;
  handleCommentChange: (comment: Comment) => void;
  handleUsageTypeChange: (usageType: string) => void;
  handleClearRelationRules: () => void;
  formErrors: Record<string, string>;
  setFormError: (key: string, error: string | null) => void;
}

const VoucherFormContext = createContext<VoucherFormContextType | undefined>(undefined);

export const useVoucherFormContext = () => {
  const context = useContext(VoucherFormContext);
  if (!context) {
    throw new Error('useVoucherFormContext must be used within a FormProvider');
  }
  return context;
};

interface FormProviderProps {
  children: ReactNode;
}

export const VoucherFormProvider = ({ children }: FormProviderProps) => {
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const setFormError = useCallback((key: string, error: string | null) => {
    setFormErrors(prev => {
      if (error === null) {
        return Object.fromEntries(Object.entries(prev).filter(([k]) => k !== key));
      }
      return { ...prev, [key]: error };
    });
  }, []);

  const [formState, setFormState] = useState<VoucherFormState>({
    activeDiscount: 'percentage_marketing',
    payload: {
      count: 1,
      specification: '',
      prefix: '',
      codeLength: 12,
      discountType: {
        type: 'percentage_marketing',
        percentage: 0.3,
      },
      validSince: toISODate(DateTime.now()),
      validUntil: getNextYear(),
      rules: [],
      entity: null,
      multiUsage: false,
    },
    comment: '',
  });

  const handlePayloadChange = ({ value, prop }: { value: PayloadTypes; prop: string }) => {
    setFormState(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        [prop]: value,
      },
    }));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, prop: string) => {
    const value = typeof e.target.value === 'number' ? parseInt(e.target.value) : e.target.value;

    handlePayloadChange({ value, prop });
  };

  const handleRuleChange = <T extends Rule>(
    updatedRule: T,
    removeRule?: boolean,
    ruleId?: string,
  ) => {
    setFormState(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        rules: removeRule
          ? prev.payload.rules.filter(r => {
              // For blackout_period with ID, filter by both type and ID
              if (r.type === 'blackout_period' && ruleId) {
                return !(r.type === updatedRule.type && r.id === ruleId);
              }
              // For allowed_relations and forbidden_relations, filter by type only (single rule per type)
              // For other rules, filter by type only
              return r.type !== updatedRule.type;
            })
          : (() => {
              // For blackout_period with ID, update by both type and ID
              if (updatedRule.type === 'blackout_period' && ruleId) {
                const filteredRules = prev.payload.rules.filter(r => {
                  if (r.type === 'blackout_period') {
                    return !(r.type === updatedRule.type && r.id === ruleId);
                  }
                  return true;
                });
                return [...filteredRules, updatedRule];
              }
              // For allowed_relations, forbidden_relations, and other rules, replace by type only
              return [...prev.payload.rules.filter(r => r.type !== updatedRule.type), updatedRule];
            })(),
      },
    }));
  };

  const handleActiveDiscountChange = (newType: string) => {
    let defaultDiscountType: DiscountType;

    switch (newType) {
      case 'percentage_marketing':
        defaultDiscountType = {
          type: newType,
          percentage: 0,
        };
        break;

      case 'credit_amount':
        defaultDiscountType = {
          type: newType,
          credit: 0,
          currency: 'EUR',
        };
        break;

      case 'value_marketing':
        defaultDiscountType = {
          type: newType,
          discount: 0,
          currency: 'EUR',
        };
        break;

      case 'free_ride_marketing':
        defaultDiscountType = {
          type: newType,
          quantity: 1,
        };
        break;

      case 'free_bike':
      case 'free_luggage':
      case 'free_seat':
        defaultDiscountType = {
          type: newType,
          quantity: 1,
        };
        break;

      case 'free_ride_hybrid':
        defaultDiscountType = {
          type: newType,
          quantity: 1,
          credit: 0,
          currency: 'EUR',
        };
        break;

      case 'value_hybrid':
        defaultDiscountType = {
          type: newType,
          discount: 0,
          credit: 0,
          currency: 'EUR',
        };
        break;
      default:
        defaultDiscountType = { type: newType };
    }

    setFormState(prev => ({
      ...prev,
      activeDiscount: newType,
      payload: {
        ...prev.payload,
        entity: null,
        discountType: defaultDiscountType,
      },
    }));
  };

  const handleEntityChange = (entity: Entity | null) => {
    setFormState(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        entity,
      },
    }));
  };

  const handleUsageTypeChange = (usageType: string) => {
    setFormState(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        multiUsage: usageType === 'multiple',
      },
    }));
  };

  const handleCommentChange = useCallback((comment: Comment) => {
    setFormState(prev => ({
      ...prev,
      comment,
    }));
  }, []);

  const handleClearRelationRules = useCallback(() => {
    setFormState(prev => ({
      ...prev,
      payload: {
        ...prev.payload,
        rules: prev.payload.rules.filter(
          r => r.type !== 'allowed_relations' && r.type !== 'forbidden_relations',
        ),
      },
    }));
  }, []);

  return (
    <VoucherFormContext.Provider
      value={{
        formState,
        setFormState,
        handleInputChange,
        handlePayloadChange,
        handleRuleChange,
        handleEntityChange,
        handleActiveDiscountChange,
        handleCommentChange,
        handleUsageTypeChange,
        handleClearRelationRules,
        formErrors,
        setFormError,
      }}
    >
      {children}
    </VoucherFormContext.Provider>
  );
};
